import {
  cancelMobilePushOperation,
  getMobilePushOperations,
  selectPrivateMoneyForMobilePushOperation,
} from 'admin/actions/mobile-push-operations';
import Select from 'admin/components/atoms/Select';
import Button from 'admin/components/shared/Button';
import Pager from 'admin/components/shared/Pager';
import MobilePushOperation from 'admin/models/mobile-push-operation';
import { formatDate } from 'admin/utils';
import * as qs from 'qs';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import { createBadge } from '../../atoms/Badge';
import Table from '../../atoms/Table';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import BreadCrumb from '../../molecules/BreadCrumb';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';

const Wrapper = styled.div`
  ${Select} {
    width: 300px;
  }
`;

const Headline = styled.div`
  margin: 10px auto;
  font-weight: bold;
  font-size: large;
  flex: auto;
`;

const Header = styled.div`
  display: flex;
  margin-right: 50px;
  direction: column;
`;

const NewButton = styled.div`
  flex: none;
  margin-bottom: 20px;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '販促管理', href: '', isCurrentPage: true },
  { value: 'プッシュメッセージ', href: '', isCurrentPage: true },
];

const HEADERS = [
  { label: '状態', size: 1.5, align: 'left' },
  { label: '件名', size: 3.5, align: 'left' },
  { label: '配信希望日時', size: 1, align: 'left' },
  { label: '完了日時', size: 1, align: 'left' },
  { label: '作成者', size: 2, align: 'left' },
  { label: '操作', size: 3, align: 'left' },
];

const MobilePushOperationStatusBadge = createBadge({
  draft: { label: '下書き', bgColor: '#585f63' },
  submitted: { label: '申請済み', bgColor: '#3c86c9' },
  preprocessing: { label: '準備中', bgColor: '#3c86c9' },
  pending: { label: '承認待ち', bgColor: '#3c86c9' },
  approved: { label: '実行待ち', bgColor: '#3c86c9' },
  rejected: { label: '却下', bgColor: '#343a40' },
  processing: { label: '実行中', bgColor: '#c89600' },
  successed: { label: '完了', bgColor: '#208637' },
  error: { label: 'エラー', bgColor: '#a51d2a' },
  canceled: { label: 'キャンセル済み', bgColor: '#585f63' },
});

const CancelButton = ({ onClick, operation }) =>
  operation.isCancelable() && <Button onClick={onClick}>キャンセル</Button>;

const MobilePushOperations = ({
  user,
  location,
  mobilePushOperation,
  selectPrivateMoneyForMobilePushOperation,
  getMobilePushOperations,
  cancelMobilePushOperation,
}) => {
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const allowedPrivateMoneys = user.issuedMoneys();
  const isSelectedMoneyAllowed = allowedPrivateMoneys.some((m) => m.id === userPrivateMoney.id);

  const { rows, pagination } = mobilePushOperation || {};
  const query = qs.parse(location.search.substr(1));
  const [page, setPage] = useState(query.page || 1);

  useEffect(() => {
    selectPrivateMoneyForMobilePushOperation(userPrivateMoney);
    getMobilePushOperations(`private_money_id=${userPrivateMoney.id}`);
  }, [userPrivateMoney.id]);

  useEffect(() => {
    getMobilePushOperations(`private_money_id=${userPrivateMoney.id}&page=${page}`);
  }, [page]);

  const onCancel = (operation) => {
    cancelMobilePushOperation(operation.id).then((response) => {
      getMobilePushOperations(`private_money_id=${userPrivateMoney.id}&page=${page}`);
    });
  };

  return (
    <Wrapper>
      <BreadCrumb crumbs={CRUMBS} />
      {isSelectedMoneyAllowed && (
        <NewButton>
          <Link to="/promotion/mobile-push-operations/create">
            <Button>新規作成</Button>
          </Link>
        </NewButton>
      )}
      <SelectedPrivateMoneyDisplay
        highlightHeader={!isSelectedMoneyAllowed}
        allowedPrivateMoneys={allowedPrivateMoneys}
      />
      {!isSelectedMoneyAllowed && <UserPrivateMoneyNotAllowed />}
      {isSelectedMoneyAllowed && (
        <>
          <Header>
            <Headline>配信履歴</Headline>
          </Header>
          <Table styles={{ marginTop: '20px' }}>
            <Table.Header>
              <Table.Row>
                {HEADERS.map((h) => (
                  <Table.Cell size={h.size} key={h.label} align={h.align}>
                    {h.label}
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows?.map((row) => {
                const operation = Object.assign(new MobilePushOperation(), row);

                return (
                  <Table.Row
                    styles={{ background: operation.status === 'canceled' ? '#ddd' : 'unset' }}
                  >
                    <Table.Cell wordBreak="keep-all">
                      <MobilePushOperationStatusBadge status={operation.status} />
                    </Table.Cell>
                    <Table.Cell>
                      <Link to={operation.getEditableURL()}>{operation.subject}</Link>
                    </Table.Cell>
                    <Table.Cell>{formatDate(operation.scheduled_at) || '-'}</Table.Cell>
                    <Table.Cell>{formatDate(operation.done_at) || '-'}</Table.Cell>
                    <Table.Cell wordBreak="break-word">{operation.operator.name}</Table.Cell>
                    <Table.Cell>
                      <CancelButton operation={operation} onClick={() => onCancel(operation)} />
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
            <Table.Footer>
              <Table.Row noBorder>
                <Table.Cell colSpan={8} align="center" styles={{ paddingTop: '16px' }}>
                  <Pager pagination={pagination || {}} onChangePage={setPage} />
                </Table.Cell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </>
      )}
    </Wrapper>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  mobilePushOperation: state.mobilePushOperation,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      selectPrivateMoneyForMobilePushOperation,
      getMobilePushOperations,
      cancelMobilePushOperation,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(MobilePushOperations);
